import { useQuery, useMutation, useQueryClient } from 'react-query';
import ApiService from '@/services/api';
import { IVendorCreateRequest, ICustomerCreateRequest, ICellUpdateRequest, IBatchCellUpdateRequest } from '@/types/psi';

// ============================================================================
// Query Keys
// ============================================================================

export const VENDOR_CUSTOMER_QUERY_KEYS = {
  vendors: ['vendors'] as const,
  customers: ['customers'] as const,
};

// ============================================================================
// Vendor Hooks
// ============================================================================

export const useVendors = () => {
  return useQuery(
    VENDOR_CUSTOMER_QUERY_KEYS.vendors,
    () => ApiService.getVendors(),
    {
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
};

export const useCreateVendor = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: IVendorCreateRequest) => ApiService.createVendor(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(VENDOR_CUSTOMER_QUERY_KEYS.vendors);
      },
    }
  );
};

export const useUpdateVendorCell = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: ICellUpdateRequest) => ApiService.updateVendorCell(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(VENDOR_CUSTOMER_QUERY_KEYS.vendors);
      },
    }
  );
};

export const useBatchUpdateVendor = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: IBatchCellUpdateRequest) => ApiService.batchUpdateVendor(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(VENDOR_CUSTOMER_QUERY_KEYS.vendors);
      },
    }
  );
};

export const useDeleteVendor = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (rowIndex: number) => ApiService.deleteVendor(rowIndex),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(VENDOR_CUSTOMER_QUERY_KEYS.vendors);
      },
    }
  );
};

// ============================================================================
// Customer Hooks
// ============================================================================

export const useCustomers = () => {
  return useQuery(
    VENDOR_CUSTOMER_QUERY_KEYS.customers,
    () => ApiService.getCustomers(),
    {
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
};

export const useCreateCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: ICustomerCreateRequest) => ApiService.createCustomer(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(VENDOR_CUSTOMER_QUERY_KEYS.customers);
      },
    }
  );
};

export const useUpdateCustomerCell = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: ICellUpdateRequest) => ApiService.updateCustomerCell(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(VENDOR_CUSTOMER_QUERY_KEYS.customers);
      },
    }
  );
};

export const useBatchUpdateCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: IBatchCellUpdateRequest) => ApiService.batchUpdateCustomer(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(VENDOR_CUSTOMER_QUERY_KEYS.customers);
      },
    }
  );
};

export const useDeleteCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (rowIndex: number) => ApiService.deleteCustomer(rowIndex),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(VENDOR_CUSTOMER_QUERY_KEYS.customers);
      },
    }
  );
};
